import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import { ReactComponent as MissionSvg } from 'images/icons/mission_play.svg'
import RobotModeIcons from 'pages/RobotList/RobotListView/RobotItem/RobotModeIcons'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import { selectRobot, selectRobotActiveMode } from 'store/selectors/robot'
import { PATH, ROBOT_MODES, ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './RobotMissionMainStatus.module.css'

enum STATES {
  SELECT_MISSION,
  MISSION_READY_TO_START,
  UPLOADING_MISSION_ERROR,
  UPLOADING,
  CALCULATING_SCENARIO,
  TURN_ON_ROBOT,
  MISSION_IS_ACTIVE,
  MISSION_IS_NOT_ACTIVE,
}

const RobotMissionMainStatus = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const robot = useSelector(selectRobot)
  const activeMode = useSelector(selectRobotActiveMode)
  const isOnline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE
  const isGausium = robot.robotInfo?.vendor === VENDOR.GAUSIUM

  const state = useMemo(() => {
    if (activeMode === null || activeMode === undefined) {
      return STATES.TURN_ON_ROBOT
    }

    if (activeMode !== ROBOT_MODES.MODE_NOT_SET) {
      return STATES.MISSION_IS_ACTIVE
    }

    if (activeMode === ROBOT_MODES.MODE_NOT_SET) {
      return STATES.MISSION_IS_NOT_ACTIVE
    }

    return STATES.TURN_ON_ROBOT
  }, [activeMode])

  const styles = {
    [`${STATES.MISSION_IS_ACTIVE}`]: style.work,
    [`${STATES.MISSION_IS_NOT_ACTIVE}`]: style.work,
  }

  const messages = {
    [STATES.TURN_ON_ROBOT]: t('robot.turn_on'),
    [STATES.MISSION_IS_ACTIVE]:
      activeMode === ROBOT_MODES.MODE_MAPPING
        ? 'Mapping in progress'
        : activeMode === ROBOT_MODES.MODE_ROUTE_RECORDING
        ? 'Route recording in progress'
        : activeMode === ROBOT_MODES.MODE_CHARGING
        ? 'Charging in progress'
        : activeMode === ROBOT_MODES.MODE_WASHING
        ? 'Washing in progress'
        : activeMode === ROBOT_MODES.MODE_PARKING
        ? 'The robot is parked'
        : activeMode === ROBOT_MODES.MODE_CALIBRATION_CHARGING ||
          activeMode === ROBOT_MODES.MODE_CALIBRATION_WASHING ||
          activeMode === ROBOT_MODES.MODE_CALIBRATION_PARKING
        ? 'Calibration in progress'
        : activeMode === ROBOT_MODES.MODE_MANUAL
        ? 'The robot in the manual mode'
        : activeMode === ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION ||
          activeMode === ROBOT_MODES.MODE_SWITCHING_TO_GNSS_BASED_LOCALIZATION ||
          activeMode === ROBOT_MODES.MODE_SWITCHING_TO_COMBINED_LOCALIZATION
        ? 'Switching localization mode'
        : activeMode === ROBOT_MODES.MODE_REBOOT_ROBOT
        ? 'Robot is rebooting'
        : activeMode === ROBOT_MODES.MODE_UPDATING_FIRMWARE
        ? 'Updating the robot firmware'
        : activeMode === ROBOT_MODES.MODE_AUTOMATIC
        ? isGausium
          ? t('robot.executing_task')
          : t('robot.active')
        : 'Unknown mode',
    [STATES.MISSION_IS_NOT_ACTIVE]: t('robot.not_active'),
  }

  // Button only for Autonomics in AUTOMATIC mode (Gausium has no mission control page)
  const isGoToMissionEnabled = activeMode === ROBOT_MODES.MODE_AUTOMATIC && !isGausium

  const onGoToMissionClick = () => {
    if (robot) {
      const path = generatePath(PATH.MISSION_CONTROL, { id: String(robot.robotInfo?.id) })
      navigate(path)
    }
  }

  if (!isOnline) {
    return null
  }

  return (
    <div>
      <div className={clsx(style.main)}>
        <div className={style.iconsWrapper}>
          {robot.robotInfo && <RobotModeIcons robot={robot.robotInfo} large hideEmergency hidePause />}
        </div>
        <div className={style.message} dangerouslySetInnerHTML={{ __html: messages[state] }} />
        <div className={style.buttonWrapper}>
          {isGoToMissionEnabled && (
            <Button.Standart className={style.buttonWide} onClick={onGoToMissionClick} disabled={!isGoToMissionEnabled}>
              <MissionSvg className={style.missionIcon} />
              {t('robot.button_go_to_mission')}
            </Button.Standart>
          )}
        </div>
      </div>
    </div>
  )
}

export default RobotMissionMainStatus
